<script setup lang="ts">
    // @ts-nocheck
    import AdvancedEditor from "@/components/Editor/AdvancedEditor.vue";
    import { useValidation } from "@/components/forms/reusables/useValidation";

    import KLabel from "@/components/forms/subComponents/Label/KLabel.vue";
    import KSuffix from "@/components/forms/subComponents/Suffix/KSuffix.vue";
    import { ref, toRefs, inject, computed } from "vue";
    import { useStore } from "@/store";

    const store = useStore();

    interface Props {
        name: string;
        label?: string;
        disabled?: boolean;
        frasValues?: Map<string, string>;
        simpleEditor?: boolean;
        withFraser?: boolean;
        enableBar?: boolean;
        typeLabel?: string;
        anmalanPatientMap?: Map<unknown, string>;
        /**
         * Ska fras automatiskt konverteras till dess värde och ersättas i content?
         */
        convertFrasToValue?: boolean;
        /**
         * Sätt till number om fältet ska visa en counter på antal tecken. Glöm inte att stoppa in max i validation!
         */
        maxCount?: number;
        validation?: string | Array<[rule: string, value?: string | number | boolean | RegExp]>;
        /**
         * KSub.vue prop Text som renderas under label
         */
        subText?: string;
        /**
         * KSub.vue prop Ikon som renderas under label
         */
        subIcon?: string;
        /**
         * KSub.vue prop Typ av subtitle
         */
        subType?: string;
        /**
         * KSuffix.vue prop Text som renderas under input
         */
        suffixText?: string;
        /**
         * KSuffix.vue prop Ikon som renderas under input
         */
        suffixIcon?: string;
        /**
         * KSuffix.vue prop Typ av subtitle
         */
        suffixType?: string;
    }

    const props = withDefaults(defineProps<Props>(), {
        disabled: false,
        convertFrasToValue: false,
        simpleEditor: false,
        withFraser: true,
        enableBar: true,
    });

    const { disabled, name, maxCount, validation, simpleEditor, withFraser, enableBar, typeLabel, anmalanPatientMap } =
        toRefs(props);

    const inputValue = ref();
    const modifiedDisabled = ref(disabled?.value);

    const count = computed(() =>
        typeof maxCount?.value === "number"
            ? {
                  max: maxCount?.value,
                  current: inputValue?.value?.length ?? 0,
              }
            : undefined
    );

    const configNames = inject<{
        moduleName: string;
        subModuleName: string;
    }>("configNames");

    const { modifiedValidation, expose: useValidationExpose } = useValidation({
        name: name.value,
        validation: validation?.value,
        moduleName: configNames?.moduleName,
        subModuleName: configNames?.subModuleName,
    });

    const setDisabled = ({ value }: { value: boolean }) => {
        modifiedDisabled.value = value;
    };

    defineExpose({
        setDisabled,
        ...useValidationExpose,
    });

    const shouldBeVisible = computed(() =>
        store.getters["config/shouldBeVisibleByName"]({
            ...(configNames ?? {}),
            name: name.value,
        })
    );

    const onUpdateContent = ({ data }: { data: unknown; errors?: unknown[] }) => {
        inputValue.value = data;
    };
</script>

<template>
    <FormulateInput
        class="k-editor"
        v-if="shouldBeVisible"
        :disabled="modifiedDisabled"
        v-model="inputValue"
        :name="name"
        type="text"
        :validation="modifiedValidation"
    >
        <template #element>
            <KLabel
                v-if="label"
                :content="label"
                :subText="subText"
                :subIcon="subIcon"
                :subType="subType"
                class="span-three"
                :validation="modifiedValidation"
            />
            <AdvancedEditor
                :content="inputValue"
                :simpleEditor="simpleEditor"
                :withFraser="withFraser"
                :enableBar="enableBar"
                :typeLabel="typeLabel"
                :anmalanPatientMap="anmalanPatientMap"
                :convertFrasToValue="convertFrasToValue"
                :frasValues="frasValues"
                @update:content="onUpdateContent"
            >
                <template #customActions="{ insertFrasChar }">
                    <div class="custom-actions"></div>
                </template>
            </AdvancedEditor>
            <KSuffix
                class="span-three"
                :count="count"
                :subText="suffixText"
                :subIcon="suffixIcon"
                :subType="suffixType"
            />
        </template>
    </FormulateInput>
</template>

<style scoped lang="scss">
    @import "@/style/form";
    @import "@/style/variables";
    .k-editor {
        ::v-deep .advanced-editor {
            .editor-container {
                width: 100%;
                max-width: 100%;
                max-height: 100%;
                border-color: $color-input-border;
                box-sizing: border-box;
                grid-column: 1 / -2;

                .editor-bar {
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                    row-gap: 0.5rem;
                    height: auto;
                }

                .editor-buttons,
                .history-buttons {
                    display: flex;
                    flex-wrap: wrap;
                    row-gap: 0.5rem;
                }

                .rich-text-editor {
                    .ProseMirror {
                        padding: $form-input-default-padding;
                        //min-height: 100%;
                        height: 100%;
                        padding: 3rem;

                        p {
                            margin-bottom: 0;
                        }
                    }
                }

                &:focus-within {
                    margin-top: -1px;
                    margin-bottom: -1px;
                    border: $input-border-focus;
                }
            }
        }

        &[data-has-errors="true"] {
            ::v-deep .advanced-editor {
                .editor-container {
                    border-bottom: $input-border-error;
                }
            }
        }
    }
</style>
